import getConfig from 'next/config';
import { setCookie, setTokenInCookies, removeCookie } from '../../hooks/useCookie';
import { setLocalStorageWithPrefix } from '../../hooks/useLocalStorage';
import { getAccountType, getLoginOperation } from '../../integration/getBrands.api';

const { publicRuntimeConfig } = getConfig();

// CRUD APIs for indexedDb

export const openEshopDB = () =>
  // eslint-disable-next-line no-new
  new Promise((resolve, reject) => {
    const request = indexedDB.open('eShop', 1);
    request.onupgradeneeded = event => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains('mfa')) {
        db.createObjectStore('mfa', { keyPath: 'id' });
      }
    };
    request.onsuccess = () => {
      resolve(request.result);
    };
    request.onerror = event => {
      console.error('indexed db error:', event.target.error);
      reject(request.error);
    };
  });

export const addDataToEshopDB = async (key, value) => {
  const db = await openEshopDB();
  const transaction = db.transaction('mfa', 'readwrite');
  const store = transaction.objectStore('mfa');

  store.put({ id: key, value });
  transaction.oncomplete = function () {
    console.log('Credential stored successfully in IndexedDB');
  };
};

export const getDataFromEshopDB = async key => {
  const db = await openEshopDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('mfa', 'readonly');
    const store = transaction.objectStore('mfa');
    // Retrieve data from eShop DB
    const getRequest = store.get(key);

    getRequest.onsuccess = function () {
      if (getRequest.result) {
        console.log('Retrieved data:', getRequest.result);
        resolve(getRequest.result);
      } else {
        console.warn('No data found in IndexedDB.');
        resolve({});
      }
    };

    getRequest.onerror = function () {
      console.error('Error retrieving data:', getRequest.error);
      reject(getRequest.error);
    };
  });
};

export const deleteDataFromEshopDB = async key => {
  const dbRequest = indexedDB.open('eShop', 1);

  dbRequest.onsuccess = () => {
    const db = dbRequest.result;
    const transaction = db.transaction('mfa', 'readwrite');
    const store = transaction.objectStore('mfa');

    const deleteRequest = store.delete(key);

    deleteRequest.onsuccess = () => {
      console.log(`Deleted record with key: ${key}`);
    };

    deleteRequest.onerror = () => {
      console.error(`Failed to delete record with key: ${key}`);
    };
  };

  dbRequest.onerror = () => {
    console.error('Failed to open database.');
  };
};

export const updateDataToEshopDB = async newData => {
  const db = await openEshopDB();
  const transaction = db.transaction('mfa', 'readwrite');
  const store = transaction.objectStore('mfa');

  const key = 'user_credential';
  const getReq = store.get(key);

  getReq.onsuccess = () => {
    const existing = getReq.result || { id: key };
    const updated = { ...existing, ...newData };
    store.put(updated);
  };
  getReq.onerror = function () {
    console.log('Failed to update data');
  };
  transaction.oncomplete = function () {
    console.log('Credential stored successfully in IndexedDB');
    db.close();
  };
  transaction.onerror = function (e) {
    console.log('Transaction Failed to update data', e);
  };
};

/**
 *
 * @name getLoginData - Function to the data from the api
 * @param {*} graphql - From useApolloClient
 * @param {String} emailAddress- User Email Address
 * @param {String} userPassword - User Password
 * @param {boolean} setShowLoader - UseState to enable or disbale loader
 * @param {boolean} setIsApiError - UseState to track the api error
 * @param {String} setLoginError - UseState to get the error message from the api when login fails
 * @returns {Object} - returns the object of response
 */

export const getLoginData = async ({
  setShowLoader,
  setOnLoadingDisablecta,
  setIsApiError,
  setLoginError,
  onload,
  setShowSubscriptionModal,
  data,
}) => {
  try {
    removeCookie('cart_Type');
    removeCookie('reference');
    removeCookie('selected_subscription');

    const { accessToken, refreshToken, refreshTokenExpiresIn: refreshExpiresIn } = data.login;

    setCookie('ION', 'refresh_Token', refreshToken, new Date().getTime() + 60 * 60 * 1000);
    await setTokenInCookies(accessToken, new Date().getTime() + 60 * 60 * 1000);
    const refreshTokenExpiresIn = refreshExpiresIn * 1000;
    const sessionExpiryTime = new Date(new Date().getTime() + refreshTokenExpiresIn).getTime();
    setCookie(
      'ION',
      'session',
      JSON.stringify({
        sessionExpiryTime,
        refreshTokenExpiresIn,
      })
    );
    setLocalStorageWithPrefix('ION_EXISTING_CUSTOMER', true);
    setIsApiError(false);
    // if (setShowLoader) setShowLoader(true);
    if (setOnLoadingDisablecta) setOnLoadingDisablecta(true);
    await onload();
    if (setShowSubscriptionModal) setShowSubscriptionModal(true);
  } catch (error) {
    console.error(error);
    if (setShowSubscriptionModal) setShowSubscriptionModal(false);
    setIsApiError(true);
    setShowLoader(false);
    setLoginError(error.message);
  }
};

function showMFAOtpModal(mfaOtpModal) {
  return new Promise(resolve => {
    mfaOtpModal.enable();

    window.resolveMFA = resolve;
  });
}

/**
 *
 * @name getOperationLogin - Function to the login api data
 * @param {*} graphql - From useApolloClient
 * @param {String} emailAddress- User Email Address
 * @param {String} userPassword - User Password
 * @param {boolean} setShowLoader, - UseState to enable or disbale loeader
 * @param {boolean} setIsApiError - UseState to track the api error
 * @param {String} setLoginError - UseState to get the error message from the api when login fails
 * @returns {Object} - returns the object of response
 */
export const getOperationLogin = async ({
  graphql,
  emailAddress,
  userPassword,
  setShowLoader,
  setOnLoadingDisablecta,
  setEnableModal,
  setLoginError,
  setIsApiError,
  onload,
  setShowSubscriptionModal,
  mobile,
  isR6Customer,
  isPrepayCustomer,
  registrationIncompleteModal,
  mfaOtpModal,
  recaptchaV3Token,
  setInvalidText,
  captchaV2,
  captchaV2Error,
  setCaptchaV2Error,
  setDisablecta,
  recaptchaRef,
  userCredential,
  setOtpUUID,
  setDelivery,
}) => {
  try {
    let billType;
    let system;

    const data = await getLoginOperation({
      graphql,
      variables: {
        email: emailAddress,
        password: userPassword,
        recaptchaToken: captchaV2 || recaptchaV3Token,
        recaptchaVersion: captchaV2 ? 'V2' : 'V3',
        deviceId:
          userCredential && userCredential.user === emailAddress && userCredential.deviceId
            ? userCredential.deviceId
            : '',
      },
    });
    updateDataToEshopDB({
      user: emailAddress,
    });

    const token = data?.login?.accessToken;
    await setTokenInCookies(token, new Date().getTime() + 60 * 60 * 1000);
    setDelivery(data?.login?.otpDelivery);

    /* eslint-disable eqeqeq */

    if (data?.login?.mfaToken != null || data?.login?.mfaToken != undefined) {
      console.log('device is trusted');
      setCookie('MFA', 'TOKEN', data?.login?.mfaToken);
      if (token.startsWith('ey')) {
        if (data?.login?.isION) {
          const type = await getAccountType({ graphql, variables: { token } });
          if (type?.accountType?.postpay) {
            billType = 'Bill';
          } else if (type?.accountType?.prepay) {
            billType = 'Prepay';
          }
        }
      } else if (token.startsWith('AQI')) {
        if (data?.login?.isR6) {
          billType = 'Fixed_Bill';
          system = 'portal';
        }
      }

      setCookie('ION', 'BILL_TYPE', billType);
      setCookie('ION', 'EMAIL_ADDRESS', emailAddress);

      const redirectToR6 = () => {
        const origin = publicRuntimeConfig.NEXT_PUBLIC_OPENCMS;
        window.location.href = `${origin}/broadband`;
        setEnableModal(false);
      };

      if (billType === 'Bill') {
        await getLoginData({
          setShowLoader,
          setOnLoadingDisablecta,
          setEnableModal,
          setIsApiError,
          setLoginError,
          onload,
          setShowSubscriptionModal,
          isPrepayCustomer,
          data,
        });
      } else if (billType === 'Prepay') {
        if (mobile) {
          await getLoginData({
            setShowLoader,
            setOnLoadingDisablecta,
            setEnableModal,
            setIsApiError,
            setLoginError,
            onload,
            setShowSubscriptionModal,
            isPrepayCustomer,
            data,
          });
        } else {
          isPrepayCustomer.enable();
        }
      } else if (billType === 'Fixed_Bill') {
        if (mobile && system === 'portal') {
          isR6Customer.enable();
          setEnableModal(false);
        } else {
          redirectToR6();
        }
      } else {
        redirectToR6();
      }
    } /* eslint-disable eqeqeq */ else if (
      data?.login?.mfaToken === null ||
      data?.login?.mfaToken === undefined
    ) {
      if (data?.login?.mfaForced === true) {
        // Show OTP flow
        console.log('MFA OTP flow');
        setOtpUUID(data?.login?.otpUuid);

        await showMFAOtpModal(mfaOtpModal);
        if (token.startsWith('ey')) {
          if (data?.login?.isION) {
            const type = await getAccountType({ graphql, variables: { token } });
            if (type?.accountType?.postpay) {
              billType = 'Bill';
            } else if (type?.accountType?.prepay) {
              billType = 'Prepay';
            }
          }
        } else if (token.startsWith('AQI')) {
          if (data?.login?.isR6) {
            billType = 'Fixed_Bill';
            system = 'portal';
          }
        }

        setCookie('ION', 'BILL_TYPE', billType);
        setCookie('ION', 'EMAIL_ADDRESS', emailAddress);

        const redirectToR6 = () => {
          const origin = publicRuntimeConfig.NEXT_PUBLIC_OPENCMS;
          window.location.href = `${origin}/broadband`;
          setEnableModal(false);
        };

        if (billType === 'Bill') {
          await getLoginData({
            setShowLoader,
            setOnLoadingDisablecta,
            setEnableModal,
            setIsApiError,
            setLoginError,
            onload,
            setShowSubscriptionModal,
            isPrepayCustomer,
            data,
          });
        } else if (billType === 'Prepay') {
          if (mobile) {
            await getLoginData({
              setShowLoader,
              setOnLoadingDisablecta,
              setEnableModal,
              setIsApiError,
              setLoginError,
              onload,
              setShowSubscriptionModal,
              isPrepayCustomer,
              data,
            });
          } else {
            isPrepayCustomer.enable();
          }
        } else if (billType === 'Fixed_Bill') {
          if (mobile && system === 'portal') {
            isR6Customer.enable();
            setEnableModal(false);
          } else {
            redirectToR6();
          }
        } else {
          redirectToR6();
        }
      } else if (data?.login?.mfaForced === false) {
        // Show NON-MFA flow
        console.log('NON MFA  flow');
        if (token.startsWith('ey')) {
          if (data?.login?.isION) {
            const type = await getAccountType({ graphql, variables: { token } });
            if (type?.accountType?.postpay) {
              billType = 'Bill';
            } else if (type?.accountType?.prepay) {
              billType = 'Prepay';
            }
          }
        } else if (token.startsWith('AQI')) {
          if (data?.login?.isR6) {
            billType = 'Fixed_Bill';
            system = 'portal';
          }
        }

        setCookie('ION', 'BILL_TYPE', billType);
        setCookie('ION', 'EMAIL_ADDRESS', emailAddress);

        const redirectToR6 = () => {
          const origin = publicRuntimeConfig.NEXT_PUBLIC_OPENCMS;
          window.location.href = `${origin}/broadband`;
          setEnableModal(false);
        };

        if (billType === 'Bill') {
          await getLoginData({
            setShowLoader,
            setOnLoadingDisablecta,
            setEnableModal,
            setIsApiError,
            setLoginError,
            onload,
            setShowSubscriptionModal,
            isPrepayCustomer,
            data,
          });
        } else if (billType === 'Prepay') {
          if (mobile) {
            await getLoginData({
              setShowLoader,
              setOnLoadingDisablecta,
              setEnableModal,
              setIsApiError,
              setLoginError,
              onload,
              setShowSubscriptionModal,
              isPrepayCustomer,
              data,
            });
          } else {
            isPrepayCustomer.enable();
          }
        } else if (billType === 'Fixed_Bill') {
          if (mobile && system === 'portal') {
            isR6Customer.enable();
            setEnableModal(false);
          } else {
            redirectToR6();
          }
        } else {
          redirectToR6();
        }
      }
    }
  } catch (error) {
    console.log(error);
    if (error?.message === 'Actions pending to complete registration') {
      registrationIncompleteModal.enable();
      setShowLoader(false);
      setEnableModal(false);
    } else if (error.message.includes('Invalid reCAPTCHA!')) {
      // setInvalidError(!invalidError);
      setShowLoader(false);
      setDisablecta(true);
      setCaptchaV2Error(!captchaV2Error);
      setInvalidText('Invalid reCAPTCHA!');
    } else {
      setDisablecta(true);
      setShowLoader(false);
      setIsApiError(true);
      setLoginError(error.message);
    }
    recaptchaRef?.current?.reset();
  }
};
